import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  IconButton,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Divider,
  Typography,
  Button,
  TextField,
  Box,
  Checkbox,
  Tooltip,
  TableSortLabel,
  DialogActions,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CalculateIcon from "@mui/icons-material/Calculate";
import SaveIcon from "@mui/icons-material/Save";
import { Person as PersonIcon } from "@mui/icons-material";
import DeleteConfirmation from "../common/DeleteConfirmation";
import CommonLoader from "../common/CommonLoader";
import { NotificationManager } from "react-notifications";
import {
  GetOrderResultsByOrderNumber,
  CalculateOrderResult,
  DeleteOrderResult,
  UpdateOrderResultAnalyticValues,
  BulkUpdateOrderResult,
} from "../../services/OrderService";
import { getComparator, stableSort } from "../../../common-utilities/sortingUtils";
import UserSelectorDialog from "../common/UserSelectorDialog";
import AddAnalysisDialog from "./AddAnalysisDialog";
import { dateToString, isNoDate } from "../../../services/dateService";

// ─── Shared compact input style ───────────────────────────────────────────────
const compactInputSx = {
  "& .MuiInputBase-root": {
    height: 26,
    fontSize: "0.72rem",
    padding: "0 4px",
  },
  "& .MuiInputBase-input": {
    padding: "2px 4px",
    fontSize: "0.72rem",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#bbb",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.65rem",
    transform: "translate(6px, 5px) scale(1)",
    "&.MuiInputLabel-shrink": {
      transform: "translate(6px, -6px) scale(0.85)",
    },
  },
};

// ─── Inline compact input cell ─────────────────────────────────────────────────
const EditCell = ({ label, value, onChange, type = "text", width = 80, highlight = false }) => (
  <TableCell sx={{ px: 0.5, py: 0.5, verticalAlign: "middle", minWidth: width }}>
    <TextField
      label={label}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      type={type}
      size="small"
      variant="outlined"
      sx={{
        ...compactInputSx,
        width: "100%",
        ...(highlight
          ? {
            "& .MuiInputBase-root": {
              ...compactInputSx["& .MuiInputBase-root"],
              backgroundColor: "#fff3cd",
            },
          }
          : {}),
      }}
      InputLabelProps={type === "datetime-local" ? { shrink: true } : undefined}
    />
  </TableCell>
);

// ─── Read-only info cell ────────────────────────────────────────────────────────
const InfoCell = ({ label, value, width = 90 }) => (
  <TableCell sx={{ px: 0.5, py: 0.5, verticalAlign: "middle", minWidth: width }}>
    <TextField
      label={label}
      value={value ?? "-"}
      size="small"
      variant="outlined"
      InputProps={{ readOnly: true, style: { backgroundColor: "#f5f5f5" } }}
      sx={{ ...compactInputSx, width: "100%" }}
    />
  </TableCell>
);

// ─── Main Component ─────────────────────────────────────────────────────────────
const ResultsTable = ({ orderNumber, accountNumber, onOrderUpdated }) => {
  const baseColumns = [
    { label: "Method", key: "methodCode", sortable: true },
    { label: "Parameter", key: "parameter", sortable: true },
    { label: "Units", key: "units", sortable: false },
    { label: "Conc.", key: "analyticalValue", sortable: true },
    { label: "Status", key: "testingStatus", sortable: true },
    { label: "Analyst", key: "analyst", sortable: true },
    { label: "Test Date", key: "dateOfAnalysis", sortable: true },
    { label: "Test Code", key: "testCode", sortable: true },
  ];

  const rowsPerPageOptions = [10, 25, 50, 100, 200];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedResultToDelete, setSelectedResultToDelete] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [editedValues, setEditedValues] = useState({});
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAnalyst, setBulkAnalyst] = useState("");
  const [bulkDate, setBulkDate] = useState("");
  const [analystSelectorOpen, setAnalystSelectorOpen] = useState(false);
  const [selectedAnalyst, setSelectedAnalyst] = useState(null);
  const [addAnalysisOpen, setAddAnalysisOpen] = useState(false);

  useEffect(() => {
    if (orderNumber) fetchOrderResults(orderNumber);
  }, [orderNumber]);

  const fetchOrderResults = async (orderNum) => {
    try {
      setLoading(true);
      const response = await GetOrderResultsByOrderNumber(orderNum);
      setResults(response.results || []);
    } catch (err) {
      NotificationManager.error("Error getting order results: " + err.message, "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (bulkDialogOpen) {
      // Default to the current date and time.
      setBulkDate(dateToString(new Date()));
    }
  }, [bulkDialogOpen]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredResults = useMemo(() => {
    if (!searchQuery) return results;
    const query = searchQuery.toLowerCase();
    return results.filter((r) =>
      Object.values(r).some((val) => String(val || "").toLowerCase().includes(query))
    );
  }, [results, searchQuery]);

  const sortedResults = useMemo(
    () => stableSort(filteredResults, getComparator(order, orderBy)),
    [filteredResults, order, orderBy]
  );

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSelectAllClick = (e) => {
    if (e.target.checked) setSelectedRows(filteredResults.map((r) => r.recordId));
    else setSelectedRows([]);
  };

  const handleOpenBulkUpdate = () => {
    if (selectedRows.length === 0) {
      NotificationManager.warning("Please select at least one result.");
      return;
    }
    setBulkDialogOpen(true);
  };

  const handleAnalystSelect = (user) => {
    if (user) {
      setSelectedAnalyst(user);
      setBulkAnalyst(user.operator);
    }
  };

  const handleBulkSave = async () => {
    if (!bulkAnalyst || !bulkDate) {
      NotificationManager.warning("Both analyst and date/time are required.");
      return;
    }
    // Send the date/time exactly as picked.
    const dto = { analyst: bulkAnalyst, dateOfAnalysis: bulkDate, recordIds: selectedRows };
    try {
      setLoading(true);
      const response = await BulkUpdateOrderResult(dto);
      if (response?.status === 200 || response?.results === true) {
        NotificationManager.success("Bulk update completed");
        setBulkDialogOpen(false);
        setBulkAnalyst("");
        setBulkDate("");
        setSelectedRows([]);
        await fetchOrderResults(orderNumber);
      } else {
        NotificationManager.error("Bulk update failed");
      }
    } catch (err) {
      NotificationManager.error(err.message || "Bulk update error");
    } finally {
      setLoading(false);
    }
  };

  const handleRowSelect = (e, recordId) => {
    e.stopPropagation();
    setSelectedRows((prev) =>
      prev.includes(recordId) ? prev.filter((id) => id !== recordId) : [...prev, recordId]
    );
  };

  const isRowSelected = (recordId) => selectedRows.includes(recordId);

  const handleCalculateSelected = async () => {
    if (selectedRows.length === 0) {
      NotificationManager.warning("Please select at least one result to calculate");
      return;
    }
    try {
      setLoading(true);
      const response = await CalculateOrderResult(selectedRows);
      if (response?.status === 200) {
        NotificationManager.success(`${selectedRows.length} result(s) calculated successfully`);
        setSelectedRows([]);
        await fetchOrderResults(orderNumber);
      } else NotificationManager.error("Error calculating results");
    } catch (error) {
      NotificationManager.error(error.response?.data?.message || error.message || "Error calculating results");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (e, resultData) => {
    e.stopPropagation();
    window.open(`/vlp/edit/lab-orders/${resultData.orderNumber}/results/${resultData.recordId}`, "_blank");
  };

  const handleAddClick = () => setAddAnalysisOpen(true);

  // Tests were saved from the Add Analysis dialog: show them, and pass on a
  // slate-driven due date change so the order form isn't left stale.
  const handleTestsAdded = async (addResult) => {
    await fetchOrderResults(orderNumber);
    if (addResult?.dueDateChanged) {
      onOrderUpdated?.({
        reportDueDate: addResult.reportDueDate,
        turnAroundTime: addResult.turnAroundTime,
      });
    }
  };

  const handleDeleteClick = (e, resultData) => {
    e.stopPropagation();
    setSelectedResultToDelete(resultData);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => setIsDeleteConfirmationOpen(false);

  const handleDeleteConfirmationConfirm = async () => {
    try {
      setLoading(true);
      const response = await DeleteOrderResult(
        selectedResultToDelete.orderNumber,
        selectedResultToDelete.recordId
      );
      if (response.results === true) {
        NotificationManager.success("Result deleted successfully");
        setIsDeleteConfirmationOpen(false);
        await fetchOrderResults(orderNumber);
      } else NotificationManager.error("Error deleting result");
    } catch (error) {
      NotificationManager.error("Error deleting result: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDynamicValueChange = (recordId, fieldKey, value) => {
    setEditedValues((prev) => ({
      ...prev,
      [recordId]: { ...(prev[recordId] || {}), [fieldKey]: value },
    }));
  };

  const handleSaveDynamicValues = async (result) => {
    const recordId = result.recordId;
    const changes = editedValues[recordId];
    if (!changes || Object.keys(changes).length === 0) {
      NotificationManager.warning("No changes to save");
      return;
    }
    try {
      setLoading(true);
      const updatePayload = {
        recordId,
        orderNumber: result.orderNumber,
        numericVal1: changes.numericVal1 ?? result.numericVal1,
        numericVal2: changes.numericVal2 ?? result.numericVal2,
        numericVal3: changes.numericVal3 ?? result.numericVal3,
        numericVal4: changes.numericVal4 ?? result.numericVal4,
        numericVal5: changes.numericVal5 ?? result.numericVal5,
        numericVal6: changes.numericVal6 ?? result.numericVal6,
        standardDeviation: changes.standardDeviation ?? result.standardDeviation,
        analyst: changes.analyst ?? result.analyst,
        dateOfAnalysis: changes.dateOfAnalysis ?? result.dateOfAnalysis,
        dateEntered: changes.dateEntered ?? result.dateEntered,
        testType: changes.testType ?? result.testType,
        units: changes.units ?? result.units,
      };
      const response = await UpdateOrderResultAnalyticValues(updatePayload);
      if (response?.status === 200 || response?.results === true) {
        NotificationManager.success("Values saved successfully");
        setEditedValues((prev) => {
          const updated = { ...prev };
          delete updated[recordId];
          return updated;
        });
        await fetchOrderResults(orderNumber);
      } else {
        NotificationManager.error("Error saving values");
      }
    } catch (error) {
      NotificationManager.error(error.response?.data?.message || error.message || "Error saving values");
    } finally {
      setLoading(false);
    }
  };

  const isAllSelected = filteredResults.length > 0 && selectedRows.length === filteredResults.length;
  const isSomeSelected = selectedRows.length > 0 && selectedRows.length < filteredResults.length;

  const formatValue = (value) =>
    value === null || value === undefined ? "-" : value instanceof Date ? value.toLocaleDateString() : String(value);

  const getStatusColor = (status) => {
    if (!status) return "inherit";
    const s = status.toLowerCase();
    if (s === "pass") return "#90EE90";
    if (s === "fail") return "#FFB6C6";
    if (s === "pend") return "#87CEEB";
    return "inherit";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const getDynamicColumnHeaders = (result) =>
    [
      { key: "numericVal1", label: result.input1?.trim() || "", inputKey: "input1" },
      { key: "numericVal2", label: result.input2?.trim() || "", inputKey: "input2" },
      { key: "numericVal3", label: result.input3?.trim() || "", inputKey: "input3" },
      { key: "numericVal4", label: result.input4?.trim() || "", inputKey: "input4" },
      { key: "numericVal5", label: result.input5?.trim() || "", inputKey: "input5" },
      { key: "numericVal6", label: result.input6?.trim() || "", inputKey: "input6" },
    ].filter((col) => col.label !== "");

  const getDynamicFieldValue = (result, fieldKey) => {
    const recordId = result.recordId;
    if (editedValues[recordId]?.[fieldKey] !== undefined) return editedValues[recordId][fieldKey];
    if (fieldKey === "dateOfAnalysis" || fieldKey === "dateEntered") {
      // Show the date/time exactly as stored; VLP's 1900 placeholder as blank.
      return isNoDate(result[fieldKey]) ? "" : String(result[fieldKey]).slice(0, 16);
    }
    const value = result[fieldKey];
    if (value === null || value === undefined || String(value).trim() === "") return "";
    return typeof value === "string" ? value.trim() : value;
  };

  const hasUnsavedChanges = (recordId) =>
    editedValues[recordId] && Object.keys(editedValues[recordId]).length > 0;

  // ─── Column span for edit row ──────────────────────────────────────────────────
  const totalColSpan = baseColumns.length + 2; // checkbox + columns + actions

  return (
    <Paper elevation={3} sx={{ padding: 3, overflow: "hidden", minWidth: 0 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Analytical Results
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search for analytical results..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          sx={{ width: 300 }}
        />
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            color="warning"
            startIcon={<EditIcon />}
            onClick={handleOpenBulkUpdate}
            disabled={selectedRows.length === 0 || loading}
          >
            Update ({selectedRows.length})
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CalculateIcon />}
            onClick={handleCalculateSelected}
            disabled={selectedRows.length === 0 || loading}
          >
            Calculate ({selectedRows.length})
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            disabled={loading}
          >
            New
          </Button>
        </Box>
      </Box>

      {loading ? (
        <CommonLoader loading={loading} />
      ) : (
        <>
          <Paper sx={{ overflowX: "auto", width: "100%" }} elevation={0}>
            <TableContainer sx={{ maxHeight: 700, overflowY: "auto", overflowX: "visible" }}>
              <Table size="small" stickyHeader sx={{ tableLayout: "auto" }}>
                {/* ── HEADER ── */}
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      sx={{ bgcolor: "primary.main", color: "white", whiteSpace: "nowrap" }}
                    >
                      <Checkbox
                        indeterminate={isSomeSelected}
                        checked={isAllSelected}
                        onChange={handleSelectAllClick}
                        sx={{ color: "white" }}
                      />
                    </TableCell>
                    {/* no expand-toggle column — all rows always open */}
                    {baseColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        sortDirection={orderBy === column.key ? order : false}
                        sx={{ bgcolor: "primary.main", color: "white", whiteSpace: "nowrap" }}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.key}
                            direction={orderBy === column.key ? order : "asc"}
                            onClick={() => handleRequestSort(column.key)}
                            sx={{
                              "&.MuiTableSortLabel-root": { color: "white" },
                              "&.MuiTableSortLabel-root:hover": { color: "white" },
                              "&.Mui-active": { color: "white" },
                              "& .MuiTableSortLabel-icon": { color: "white !important" },
                            }}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                    <TableCell sx={{ bgcolor: "primary.main", color: "white", whiteSpace: "nowrap" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                {/* ── BODY ── */}
                <TableBody>
                  {sortedResults
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((result, index) => {
                      const isSelected = isRowSelected(result.recordId);
                      const dynamicColumns = getDynamicColumnHeaders(result);
                      const hasChanges = hasUnsavedChanges(result.recordId);

                      return (
                        <React.Fragment key={result.recordId}>
                          {/* ── Main data row ── */}
                          <TableRow
                            sx={{
                              backgroundColor: isSelected
                                ? "rgba(25, 118, 210, 0.12)"
                                : index % 2 === 0
                                  ? "rgba(0,0,0,0.04)"
                                  : "white",
                              "&:hover": { backgroundColor: "rgba(0,0,0,0.08)" },
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) => handleRowSelect(e, result.recordId)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </TableCell>
                            {baseColumns.map((column) => (
                              <TableCell
                                key={column.key}
                                sx={{
                                  backgroundColor:
                                    column.key === "testingStatus"
                                      ? getStatusColor(result[column.key])
                                      : "inherit",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {column.key === "dateOfAnalysis"
                                  ? (isNoDate(result[column.key]) ? "-" : formatDate(result[column.key]))
                                  : formatValue(result[column.key])}
                              </TableCell>
                            ))}
                            <TableCell>
                              <Box display="flex" gap={0.5}>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleEditClick(e, result)}
                                    sx={{ color: "orange" }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="View">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(
                                        `/vlp/lab-orders/${result.orderNumber}/samples/${result.sampleNumber}/method-code/${result.methodCode}`,
                                        "_blank"
                                      );
                                    }}
                                    sx={{ color: "green" }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleDeleteClick(e, result)}
                                    sx={{ color: "red" }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* ── Always-visible inline edit row ── */}
                          <TableRow>
                            <TableCell
                              colSpan={totalColSpan}
                              sx={{ p: 0, borderBottom: "2px solid #c8d8ec" }}
                            >
                              <Box
                                sx={{
                                  bgcolor: "#eef4fb",
                                  borderLeft: "3px solid #1976d2",
                                  px: 1,
                                  py: 0.5,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mb: 1,
                                  maxWidth: "100%",
                                  overflowX: "hidden",
                                }}
                              >
                                {/* Save button — only when there are pending changes */}
                                {hasChanges && (
                                  <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    startIcon={<SaveIcon />}
                                    sx={{ height: 26, fontSize: "0.7rem", py: 0, px: 1, flexShrink: 0 }}
                                    onClick={() => handleSaveDynamicValues(result)}
                                  >
                                    Save
                                  </Button>
                                )}

                                {/* Compact inline edit table */}
                                <Table
                                  size="small"
                                  sx={{
                                    tableLayout: "auto",
                                    "& td": { border: "1px solid #c8d8ec", bgcolor: "white", p: 0 },
                                    "& th": {
                                      border: "1px solid #c8d8ec",
                                      bgcolor: "#d0e4f7",
                                      color: "#1565c0",
                                      fontWeight: 700,
                                      fontSize: "0.68rem",
                                      py: 0.3,
                                      px: 0.5,
                                      whiteSpace: "nowrap",
                                      textAlign: "center",
                                    },
                                  }}
                                >
                                  <TableHead>
                                    <TableRow>
                                      {dynamicColumns.map((col) => (
                                        <TableCell component="th" key={col.key}>
                                          {col.label}
                                        </TableCell>
                                      ))}
                                      <TableCell component="th">Analyst</TableCell>
                                      <TableCell component="th" sx={{ minWidth: 155 }}>Test Date &amp; Time</TableCell>
                                      <TableCell component="th" sx={{ minWidth: 155 }}>Date Entered</TableCell>
                                      <TableCell component="th">Type</TableCell>
                                      <TableCell component="th">Unit</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    <TableRow>
                                      {dynamicColumns.map((col) => (
                                        <EditCell
                                          key={col.key}
                                          label=""
                                          value={getDynamicFieldValue(result, col.key)}
                                          onChange={(val) => handleDynamicValueChange(result.recordId, col.key, val)}
                                          width={70}
                                          highlight={hasChanges && editedValues[result.recordId]?.[col.key] !== undefined}
                                        />
                                      ))}
                                      <EditCell
                                        label=""
                                        value={getDynamicFieldValue(result, "analyst")}
                                        onChange={(val) => handleDynamicValueChange(result.recordId, "analyst", val)}
                                        width={80}
                                        highlight={hasChanges && editedValues[result.recordId]?.analyst !== undefined}
                                      />
                                      <EditCell
                                        label=""
                                        type="datetime-local"
                                        value={getDynamicFieldValue(result, "dateOfAnalysis") || ""}
                                        onChange={(val) => handleDynamicValueChange(result.recordId, "dateOfAnalysis", val)}
                                        width={155}
                                        highlight={hasChanges && editedValues[result.recordId]?.dateOfAnalysis !== undefined}
                                      />
                                      {/* Read-only, as in VLP: the server stamps it when results are entered. */}
                                      <InfoCell
                                        label=""
                                        value={getDynamicFieldValue(result, "dateEntered").replace("T", " ") || "-"}
                                        width={155}
                                      />
                                      <EditCell
                                        label=""
                                        value={getDynamicFieldValue(result, "testType")}
                                        onChange={(val) => handleDynamicValueChange(result.recordId, "testType", val)}
                                        width={70}
                                        highlight={hasChanges && editedValues[result.recordId]?.testType !== undefined}
                                      />
                                      <EditCell
                                        label=""
                                        value={getDynamicFieldValue(result, "units")}
                                        onChange={(val) => handleDynamicValueChange(result.recordId, "units", val)}
                                        width={70}
                                        highlight={hasChanges && editedValues[result.recordId]?.units !== undefined}
                                      />
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </Box>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}

                  {filteredResults.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={totalColSpan} align="center">
                        No results found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredResults.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </>
      )}
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this result?"
        color="red"
      />

      {/* Bulk Update Dialog */}
      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Selected Results</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Analyst"
            required
            error={!bulkAnalyst}
            value={
              selectedAnalyst
                ? `${selectedAnalyst.operator.trim()}${selectedAnalyst.aliasName ? ` - ${selectedAnalyst.aliasName.trim()}` : ""
                }`
                : "Click to select analyst"
            }
            fullWidth
            size="small"
            onClick={() => setAnalystSelectorOpen(true)}
            InputProps={{
              readOnly: true,
              style: { cursor: "pointer" },
              startAdornment: !selectedAnalyst && (
                <InputAdornment position="start">
                  <PersonIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: selectedAnalyst && (
                <InputAdornment position="end">
                  <EditIcon color="primary" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            helperText={!bulkAnalyst ? "Required — click to select analyst" : "Click to change analyst"}
          />
          <TextField
            label="Date & Time of Analysis"
            type="datetime-local"
            required
            error={!bulkDate}
            fullWidth
            size="small"
            value={bulkDate}
            onChange={(e) => setBulkDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <UserSelectorDialog
        open={analystSelectorOpen}
        onClose={() => setAnalystSelectorOpen(false)}
        onSelect={handleAnalystSelect}
        multiSelect={false}
      />

      <AddAnalysisDialog
        open={addAnalysisOpen}
        onClose={() => setAddAnalysisOpen(false)}
        orderNumber={orderNumber}
        accountNumber={accountNumber}
        onTestsAdded={handleTestsAdded}
      />
    </Paper>
  );
};

export default ResultsTable;